// GCP Compute Engine on-demand list prices for the GPU machine shapes that
// can run pool workers (g2 / g4 / n1+T4). Snapshot of the Cloud Billing
// catalog taken 2026-05-15 — pull again if numbers look stale.
//
// Per-resource unit prices below are USD/hr for compute & GPU SKUs, and
// USD/GB-month for disk SKUs. The compute model is identical to GCE's:
//   total = vCPU * vCpuPrice
//         + ramGb * ramPrice
//         + gpuCount * gpuPrice * gpuFraction   (fraction = 1 for full GPU)
//         + (windows ? vCPU * 0.046 : 0)
//         + (gpu vWS-licensed ? gpuCount * 0.20 : 0)
//         + diskGb * pdSsdPrice / 730
//         + localSsdGb * localSsdPrice / 730
//
// Sustained-use discounts (SUD) auto-apply at billing time and are NOT
// reflected here — these are list prices.

export const HOURS_PER_MONTH = 730;
export const WINDOWS_LICENSE_PER_VCPU_HR = 0.046;
export const VWS_LICENSE_PER_GPU_HR = 0.2;

export interface RegionPricing {
  // G2 family (L4 bundled, sold separately on the bill)
  g2Core?: number; // $/vCPU/hr
  g2Ram?: number; // $/GB-RAM/hr
  l4Gpu?: number; // $/GPU/hr
  // G4 family (RTX PRO 6000 Blackwell, local SSD bundled $0)
  g4Core?: number;
  g4Ram?: number;
  rtxPro6000?: number; // $/GPU/hr, billed at fraction for MIG slices
  // N1 family (T4 vWS commonly paired)
  n1Core?: number;
  n1Ram?: number;
  t4Gpu?: number; // $/GPU/hr (vWS license is a separate global SKU @ $0.20)
  // Persistent disks ($/GB-month)
  pdSsd?: number;
  pdBalanced?: number;
  pdStandard?: number;
  // Local SSD scratch ($/GB-month)
  localSsd?: number;
}

// Note on coverage: a region appears here only for the SKUs we've verified
// against the live billing catalog. Shapes that depend on a missing SKU
// (e.g. G4 in europe-west6) won't render a price — they'll show "—".
export const REGION_PRICING: Record<string, RegionPricing> = {
  // Americas (Iowa / Oregon / S. Carolina — Americas SKU group)
  'us-central1': {
    g2Core: 0.024988, g2Ram: 0.002927, l4Gpu: 0.560040,
    g4Core: 0.04891, g4Ram: 0.00587, rtxPro6000: 1.09565,
    n1Core: 0.031611, n1Ram: 0.004237, t4Gpu: 0.35,
    pdSsd: 0.17, pdBalanced: 0.1, pdStandard: 0.04, localSsd: 0.08,
  },
  'us-west1': {
    g2Core: 0.024988, g2Ram: 0.002927, l4Gpu: 0.560040,
    g4Core: 0.04891, g4Ram: 0.00587, rtxPro6000: 1.09565,
    n1Core: 0.031611, n1Ram: 0.004237, t4Gpu: 0.35,
    pdSsd: 0.17, pdBalanced: 0.1, pdStandard: 0.04, localSsd: 0.08,
  },
  'us-east1': {
    g2Core: 0.024988, g2Ram: 0.002927, l4Gpu: 0.560040,
    g4Core: 0.04891, g4Ram: 0.00587, rtxPro6000: 1.09565,
    n1Core: 0.031611, n1Ram: 0.004237, t4Gpu: 0.35,
    pdSsd: 0.17, pdBalanced: 0.1, pdStandard: 0.04, localSsd: 0.08,
  },
  // Virginia — distinct SKU group from Americas
  'us-east4': {
    g2Core: 0.024906, g2Ram: 0.002918, l4Gpu: 0.558206,
    g4Core: 0.04891, g4Ram: 0.00587, rtxPro6000: 1.09565,
    pdSsd: 0.187, pdBalanced: 0.11, pdStandard: 0.044, localSsd: 0.088,
  },
  // Columbus / Salt Lake — premium tier
  'us-east5': {
    g4Core: 0.053801, g4Ram: 0.006457, rtxPro6000: 1.205215,
  },
  'us-south1': {
    g4Core: 0.04891, g4Ram: 0.00587, rtxPro6000: 1.09565,
  },
  'us-west3': {
    g4Core: 0.053801, g4Ram: 0.006457, rtxPro6000: 1.205215,
  },
  // Europe (mid-tier)
  'europe-west1': {
    g4Core: 0.053801, g4Ram: 0.006457, rtxPro6000: 1.205215,
  },
  'europe-west2': {
    g2Core: 0.028451, g2Ram: 0.003333, l4Gpu: 0.637654,
    g4Core: 0.058692, g4Ram: 0.007044, rtxPro6000: 1.31478,
    pdSsd: 0.204, pdBalanced: 0.12, pdStandard: 0.048, localSsd: 0.096,
  },
  'europe-west4': {
    g2Core: 0.026264, g2Ram: 0.003077, l4Gpu: 0.588630,
    g4Core: 0.053801, g4Ram: 0.006457, rtxPro6000: 1.205215,
    pdSsd: 0.187, pdBalanced: 0.11, pdStandard: 0.044, localSsd: 0.088,
  },
  'europe-west6': {
    g2Core: 0.032736, g2Ram: 0.003835, l4Gpu: 0.783440,
    pdSsd: 0.221, pdBalanced: 0.13, pdStandard: 0.052, localSsd: 0.104,
  },
  'europe-west8': {
    g4Core: 0.058692, g4Ram: 0.007044, rtxPro6000: 1.31478,
  },
  'europe-north1': {
    g4Core: 0.053801, g4Ram: 0.006457, rtxPro6000: 1.205215,
  },
  // APAC
  'asia-northeast1': {
    g2Core: 0.032090, g2Ram: 0.003759, l4Gpu: 0.719204,
    pdSsd: 0.221, pdBalanced: 0.13, pdStandard: 0.052, localSsd: 0.104,
  },
  'asia-southeast1': {
    g4Core: 0.058692, g4Ram: 0.007044, rtxPro6000: 1.31478,
  },
  // Oceania
  'australia-southeast1': {
    n1Core: 0.044856, n1Ram: 0.006011, t4Gpu: 0.44,
    pdSsd: 0.23, pdBalanced: 0.135, pdStandard: 0.054, localSsd: 0.108,
  },
};

export const REGION_LABELS: Record<string, string> = {
  'us-central1': 'US Central (Iowa)',
  'us-west1': 'US West (Oregon)',
  'us-east1': 'US East (S. Carolina)',
  'us-east4': 'US East (N. Virginia)',
  'us-east5': 'US East (Columbus)',
  'us-south1': 'US South (Dallas)',
  'us-west3': 'US West (Salt Lake)',
  'europe-west1': 'Europe (Belgium)',
  'europe-west2': 'Europe (London)',
  'europe-west4': 'Europe (Netherlands)',
  'europe-west6': 'Europe (Zurich)',
  'europe-west8': 'Europe (Milan)',
  'europe-north1': 'Europe (Finland)',
  'asia-northeast1': 'Asia (Tokyo)',
  'asia-southeast1': 'Asia (Singapore)',
  'australia-southeast1': 'Australia (Sydney)',
};

export type MachineFamily = 'g2' | 'g4' | 'n1';
export type GpuKind = 'l4' | 'rtx-pro-6000' | 't4-vws';

export interface MachineSpec {
  name: string;
  family: MachineFamily;
  vcpus: number;
  ramGb: number;
  gpu: {
    kind: GpuKind;
    /** Logical GPU count (an integer; fractional MIG slices keep count=1
     *  and use `fraction` < 1 to represent the partition). */
    count: number;
    /** GPU-time multiplier for MIG-slice shapes. Undefined = full GPU. */
    fraction?: number;
    vramGb: number;
  };
  /** Local SSD that must be attached separately and billed per-GB. */
  attachedLocalSsdGb: number;
  /** Local SSD bundled into the shape price ($0 SKU). Display-only. */
  bundledLocalSsdGb?: number;
}

export const MACHINE_SPECS: MachineSpec[] = [
  // G2 — NVIDIA L4 (Ada Lovelace), bundled 1×L4 per shape
  { name: 'g2-standard-4',  family: 'g2', vcpus: 4,  ramGb: 16,
    gpu: { kind: 'l4', count: 1, vramGb: 24 }, attachedLocalSsdGb: 375 },
  { name: 'g2-standard-8',  family: 'g2', vcpus: 8,  ramGb: 32,
    gpu: { kind: 'l4', count: 1, vramGb: 24 }, attachedLocalSsdGb: 375 },
  { name: 'g2-standard-12', family: 'g2', vcpus: 12, ramGb: 48,
    gpu: { kind: 'l4', count: 1, vramGb: 24 }, attachedLocalSsdGb: 375 },
  { name: 'g2-standard-16', family: 'g2', vcpus: 16, ramGb: 64,
    gpu: { kind: 'l4', count: 1, vramGb: 24 }, attachedLocalSsdGb: 375 },

  // G4 — NVIDIA RTX PRO 6000 Blackwell. Fractional shapes are MIG slices
  // of one physical GPU; full + multi-GPU shapes hold whole GPUs.
  { name: 'g4-standard-6',  family: 'g4', vcpus: 6,  ramGb: 22,
    gpu: { kind: 'rtx-pro-6000', count: 1, fraction: 0.125, vramGb: 12 },
    attachedLocalSsdGb: 0, bundledLocalSsdGb: 375 },
  { name: 'g4-standard-12', family: 'g4', vcpus: 12, ramGb: 45,
    gpu: { kind: 'rtx-pro-6000', count: 1, fraction: 0.25, vramGb: 24 },
    attachedLocalSsdGb: 0, bundledLocalSsdGb: 375 },
  { name: 'g4-standard-24', family: 'g4', vcpus: 24, ramGb: 90,
    gpu: { kind: 'rtx-pro-6000', count: 1, fraction: 0.5, vramGb: 48 },
    attachedLocalSsdGb: 0, bundledLocalSsdGb: 750 },
  { name: 'g4-standard-48', family: 'g4', vcpus: 48, ramGb: 180,
    gpu: { kind: 'rtx-pro-6000', count: 1, vramGb: 96 },
    attachedLocalSsdGb: 0, bundledLocalSsdGb: 1500 },
  { name: 'g4-standard-96', family: 'g4', vcpus: 96, ramGb: 360,
    gpu: { kind: 'rtx-pro-6000', count: 2, vramGb: 192 },
    attachedLocalSsdGb: 0, bundledLocalSsdGb: 3000 },

  // N1 + attached T4 vWS — Turing-era, kept for legacy parity
  { name: 'n1-standard-4',  family: 'n1', vcpus: 4,  ramGb: 15,
    gpu: { kind: 't4-vws', count: 1, vramGb: 16 }, attachedLocalSsdGb: 375 },
  { name: 'n1-standard-8',  family: 'n1', vcpus: 8,  ramGb: 30,
    gpu: { kind: 't4-vws', count: 1, vramGb: 16 }, attachedLocalSsdGb: 375 },
  { name: 'n1-standard-16', family: 'n1', vcpus: 16, ramGb: 60,
    gpu: { kind: 't4-vws', count: 1, vramGb: 16 }, attachedLocalSsdGb: 375 },
];

export interface PriceBreakdown {
  /** Sum of vCPU + RAM line items, $/hr */
  compute: number;
  /** GPU line item (fraction-adjusted), $/hr */
  gpu: number;
  /** Windows Server 2025 Datacenter license, $/hr (0 if Linux) */
  windows: number;
  /** NVIDIA vWS license for T4-vWS, $/hr (0 unless vWS) */
  vws: number;
  /** Disk line items (boot pd-ssd + attached local SSD), $/hr */
  disks: number;
  total: number;
}

export interface PriceInputs {
  withWindows: boolean;
  bootDiskGb: number;
}

/** Returns null when the region doesn't list pricing for this shape's
 *  family — the caller should render a `—` in the matrix cell. */
export function computePrice(
  spec: MachineSpec,
  region: string,
  inputs: PriceInputs,
): PriceBreakdown | null {
  const rp = REGION_PRICING[region];
  if (!rp) return null;

  let compute = 0;
  let gpu = 0;
  let vws = 0;

  if (spec.family === 'g2') {
    if (rp.g2Core == null || rp.g2Ram == null || rp.l4Gpu == null) return null;
    compute = spec.vcpus * rp.g2Core + spec.ramGb * rp.g2Ram;
    gpu = spec.gpu.count * rp.l4Gpu * (spec.gpu.fraction ?? 1);
  } else if (spec.family === 'g4') {
    if (rp.g4Core == null || rp.g4Ram == null || rp.rtxPro6000 == null) return null;
    compute = spec.vcpus * rp.g4Core + spec.ramGb * rp.g4Ram;
    gpu = spec.gpu.count * rp.rtxPro6000 * (spec.gpu.fraction ?? 1);
  } else {
    if (rp.n1Core == null || rp.n1Ram == null || rp.t4Gpu == null) return null;
    compute = spec.vcpus * rp.n1Core + spec.ramGb * rp.n1Ram;
    gpu = spec.gpu.count * rp.t4Gpu;
    vws = spec.gpu.count * VWS_LICENSE_PER_GPU_HR;
  }

  const windows = inputs.withWindows ? spec.vcpus * WINDOWS_LICENSE_PER_VCPU_HR : 0;

  let disks = 0;
  if (rp.pdSsd != null) {
    disks += (inputs.bootDiskGb * rp.pdSsd) / HOURS_PER_MONTH;
  }
  if (spec.attachedLocalSsdGb > 0 && rp.localSsd != null) {
    disks += (spec.attachedLocalSsdGb * rp.localSsd) / HOURS_PER_MONTH;
  }

  const total = compute + gpu + windows + vws + disks;
  return { compute, gpu, windows, vws, disks, total };
}

/** Indicative relative graphics+encode score vs. an L4 baseline (=1.0).
 *  These are coarse ratios derived from public benchmarks (3DMark / NVENC
 *  throughput) — useful for relative perf/$ ranking, not absolute claims. */
export function relativePerf(spec: MachineSpec): number {
  if (spec.gpu.kind === 'l4') return 1.0;
  if (spec.gpu.kind === 't4-vws') return 0.33;
  // RTX PRO 6000 Blackwell — full GPU benchmarks ~3–4× an L4 for rasterized
  // workloads + 3× NVENC throughput. Slice proportionally for MIG shapes.
  const full = 3.5;
  return full * (spec.gpu.fraction ?? 1) * spec.gpu.count;
}
